import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Clock, CheckCircle2, ShieldCheck, Send, PenSquare, Inbox, MessageSquare } from "lucide-react";
import { api } from "@/lib/appApi";
import { useAuth } from "@/context/AuthContext";
import { STATUS_LABELS, ROLE_LABELS } from "@/lib/adminConst";

const ICONS = { published: CheckCircle2, pending_review: Clock, verified: ShieldCheck, approved: Send, draft: FileText, needs_revision: PenSquare, archived: FileText };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);

  if (user?.role === "pending" || err) {
    return (
      <div className="max-w-lg mx-auto mt-16 bg-white rounded-xl border border-amber-200 p-8 text-center">
        <ShieldCheck className="h-12 w-12 text-amber-400 mx-auto mb-4" />
        <h1 className="font-serif text-2xl font-bold text-dlh-green">Akun Menunggu Penetapan Peran</h1>
        <p className="text-gray-600 mt-3">Akun Anda ({user?.email}) belum memiliki peran. Silakan hubungi Super Admin portal untuk mendapatkan akses menulis atau memeriksa konten.</p>
      </div>
    );
  }
  if (!data) return <p className="text-gray-500">Memuat…</p>;

  const c = data.counts;
  const cards = [
    { key: "published", label: "Terbit", to: "/admin/konten?status=published" },
    { key: "pending_review", label: "Menunggu Pemeriksaan", to: "/admin/antrean" },
    { key: "verified", label: "Terverifikasi", to: "/admin/antrean" },
    { key: "approved", label: "Disetujui (siap terbit)", to: "/admin/antrean" },
    { key: "draft", label: "Draft", to: "/admin/konten?status=draft" },
    { key: "needs_revision", label: "Perlu Revisi", to: "/admin/antrean" },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-extrabold text-dlh-green">Dashboard</h1>
        <p className="text-gray-500 mt-1">Selamat datang, {user?.name} · {ROLE_LABELS[user?.role]}</p>
      </div>

      {data.new_comments > 0 && (
        <Link to="/admin/komentar" data-testid="dashboard-new-comments"
          className="mb-6 flex items-center gap-3 bg-dlh-orange/10 border border-dlh-orange/40 rounded-xl p-4 hover:bg-dlh-orange/15 transition-colors">
          <div className="h-10 w-10 rounded-full bg-dlh-orange text-white flex items-center justify-center shrink-0"><MessageSquare className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold text-dlh-green">{data.new_comments} tanggapan pemirsa baru perlu ditinjau</p>
            <p className="text-sm text-gray-500">Klik untuk membuka daftar tanggapan dan meninjau.</p>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => {
          const Icon = ICONS[card.key] || FileText;
          return (
            <Link key={card.key} to={card.to} data-testid={`stat-${card.key}`}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]">
              <div className="flex items-center justify-between">
                <Icon className="h-6 w-6 text-dlh-greenMid" />
                <span className="text-3xl font-extrabold text-dlh-green">{c[card.key] ?? 0}</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">{card.label}</p>
            </Link>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/admin/konten/baru" className="inline-flex items-center gap-2 bg-dlh-green text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-dlh-greenDark"><PenSquare className="h-4 w-4" /> Tulis Berita Baru</Link>
        <Link to="/admin/antrean" className="inline-flex items-center gap-2 bg-white border border-gray-300 text-dlh-green px-5 py-2.5 rounded-lg font-semibold hover:bg-gray-50"><Inbox className="h-4 w-4" /> Antrean Pemeriksaan</Link>
      </div>

      <p className="mt-8 text-xs text-gray-400">Jumlah dihitung sesuai hak akses Anda. Portal tidak menampilkan statistik pembaca palsu.</p>
    </div>
  );
}
